import React, { useState, useEffect } from 'react'
import { Users, Plus, Key, BarChart3, Settings, LogOut, Play, Share2, Wrench } from 'lucide-react'
import InvitesTab from '../components/InvitesTab'
import UsersTab from '../components/UsersTab'
import VideosTab from '../components/VideosTab'
import PublicLinksTab from '../components/PublicLinksTab'
import MaintenanceTab from '../components/MaintenanceTabFinal'
import AdminLogin from '../components/AdminLogin'

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [adminUser, setAdminUser] = useState(null)
  const [activeTab, setActiveTab] = useState('overview')
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalVideos: 0,
    totalStorage: 0,
    activeInvites: 0
  })

  useEffect(() => {
    checkAdminAuth()
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      loadAdminStats()
    }
  }, [isAuthenticated])

  const checkAdminAuth = async () => {
    // Consolidated token management - use only 'token' key
    const token = localStorage.getItem('token')
    const user = localStorage.getItem('user')

    if (!token || !user) {
      clearAdminAuth()
      return
    }

    try {
      const parsedUser = JSON.parse(user)
      if (!(parsedUser.role === 'admin' || parsedUser.type === 'admin')) {
        clearAdminAuth()
        return
      }

      // Validate token with server
      const isValid = await validateAdminToken(token)
      if (isValid) {
        setAdminUser(parsedUser)
        setIsAuthenticated(true)
      } else {
        console.log('❌ Admin token expired, requiring re-login')
        clearAdminAuth()
      }
    } catch (error) {
      console.error('Error validating admin auth:', error)
      clearAdminAuth()
    }
  }

  const validateAdminToken = async (token) => {
    try {
      // Use existing admin endpoint to validate token
      const response = await fetch('/.netlify/functions/admin-list-users-v2', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      return response.ok
    } catch (error) {
      console.error('Token validation failed:', error)
      return false
    }
  }

  const clearAdminAuth = () => {
    // Clear all possible token variations for cleanup
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    localStorage.removeItem('adminToken')
    localStorage.removeItem('vsw_auth_token')
    localStorage.removeItem('vsw_refresh_token')
    setIsAuthenticated(false)
    setAdminUser(null)
  }

  const handleLogin = (user, token) => {
    // Store admin token properly
    localStorage.setItem('token', token)
    localStorage.setItem('user', JSON.stringify(user))
    setAdminUser(user)
    setIsAuthenticated(true)
    // Load stats after successful login
    setTimeout(loadAdminStats, 100)
  }

  const handleLogout = () => {
    clearAdminAuth()
  }

  const refreshAdminStats = async () => {
    await loadAdminStats()
  }

  const loadAdminStats = async () => {
    try {
      // Check for standard token
      const token = localStorage.getItem('token')

      console.log('Loading admin stats, token present:', !!token)

      if (!token) {
        console.error('No authentication token found for admin stats')
        setStats({
          totalUsers: 'Auth Required',
          totalVideos: 'Auth Required',
          totalStorage: 'Auth Required',
          activeInvites: 'Auth Required'
        })
        return
      }

      console.log('Making admin stats request with token')
      const response = await fetch('/.netlify/functions/admin-stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      console.log('Admin stats response status:', response.status)

      const data = await response.json()
      console.log('Admin stats response data:', data)

      if (data.success && data.stats) {
        console.log('Setting admin stats:', data.stats)
        setStats(data.stats)
      } else {
        console.error('Failed to load admin stats:', data.errors || data.error)
        // Fallback to basic stats if API fails
        setStats({
          totalUsers: 'N/A',
          totalVideos: 'N/A',
          totalStorage: 'N/A',
          activeInvites: 'N/A'
        })
      }
    } catch (error) {
      console.error('Error loading admin stats:', error)
      // Fallback to basic stats if API fails
      setStats({
        totalUsers: 'N/A',
        totalVideos: 'N/A',
        totalStorage: 'N/A',
        activeInvites: 'N/A'
      })
    }
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'videos', label: 'Videos', icon: Play },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'invites', label: 'Invites', icon: Key },
    { id: 'public-links', label: 'Public Links', icon: Share2 },
    { id: 'maintenance', label: 'Maintenance', icon: Wrench },
    { id: 'settings', label: 'Settings', icon: Settings }
  ]

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">WOW Admin Dashboard</h1>
          <p className="text-gray-400">Manage users, invitations, and platform settings</p>
        </div>
        <div className="flex items-center space-x-4">
          <span className="text-gray-400">Welcome, {adminUser?.username}</span>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-8">
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                activeTab === tab.id
                  ? 'bg-netflix-red text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          )
        })}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Users</p>
                  <p className="text-2xl font-bold text-white">{stats.totalUsers}</p>
                </div>
                <Users className="w-8 h-8 text-netflix-red" />
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Videos</p>
                  <p className="text-2xl font-bold text-white">{stats.totalVideos}</p>
                </div>
                <div className="w-8 h-8 text-netflix-red text-2xl">📹</div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Storage</p>
                  <p className="text-2xl font-bold text-white">{stats.totalStorage}</p>
                </div>
                <div className="w-8 h-8 text-netflix-red text-2xl">💾</div>
              </div>
            </div>

            <div className="card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Active Invites</p>
                  <p className="text-2xl font-bold text-white">{stats.activeInvites}</p>
                </div>
                <Key className="w-8 h-8 text-netflix-red" />
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="card p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {[
                { action: 'New user registered', user: 'john@example.com', time: '2 hours ago' },
                { action: 'Video uploaded', user: 'sarah@example.com', time: '4 hours ago' },
                { action: 'Invite code created', user: 'admin', time: '1 day ago' }
              ].map((activity, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-b-0">
                  <div>
                    <p className="text-white text-sm">{activity.action}</p>
                    <p className="text-gray-400 text-xs">{activity.user}</p>
                  </div>
                  <span className="text-gray-500 text-xs">{activity.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Videos Tab */}
      {activeTab === 'videos' && <VideosTab onStatsChange={refreshAdminStats} />}

      {/* Users Tab */}
      {activeTab === 'users' && <UsersTab onStatsChange={refreshAdminStats} />}

      {/* Invites Tab */}
      {activeTab === 'invites' && <InvitesTab onStatsChange={refreshAdminStats} />}

      {/* Public Links Tab */}
      {activeTab === 'public-links' && <PublicLinksTab />}

      {/* Maintenance Tab */}
      {activeTab === 'maintenance' && <MaintenanceTab />}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="card p-6">
          <h3 className="text-xl font-semibold text-white mb-6">Platform Settings</h3>

          <div className="text-center py-12">
            <Settings className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Platform settings interface will be implemented here</p>
          </div>
        </div>
      )}

    </div>
  )
}

export default AdminPage